import { isEqual } from 'lodash/fp';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { ErrorType } from 'dashboard/components/PlatformSchemaEditor/constants';
import PropertyRow from 'dashboard/components/PlatformSchemaEditor/PropertyRow';
import { createNewProperty } from 'dashboard/components/PlatformSchemaEditor/utils';
import { PlatformJsonSchema } from 'dashboard/lib/workflows/platform-object';
import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { JsonSchemaCore, JsonSchemaObject, JsonSchemaTypes } from 'lib/json-schema';
import { generateWorkflowToken } from 'lib/workflows';

interface SchemaPropertiesProps {
  editableId?: string;
  jsonSchema: JsonSchemaObject<PlatformJsonSchema>;
  nestLevel?: number;
  prefix?: string;
  includePersonaObjects: boolean;
  templateOptionsByObjectKey: Partial<
    Record<WorkflowFieldObjectKey, { label: string; value: string }[]>
  >;
  setEditableId: (id: string | undefined) => void;
  setOutsideClickable: (val: boolean) => void;
  updateJsonSchema: (jsonSchema: JsonSchemaObject<PlatformJsonSchema>) => void;
  addError: (id: string, error: ErrorType[]) => void;
}

const SchemaProperties: FC<SchemaPropertiesProps> = ({
  editableId,
  jsonSchema,
  nestLevel = 0,
  prefix = '',
  includePersonaObjects,
  templateOptionsByObjectKey,
  setEditableId,
  setOutsideClickable,
  updateJsonSchema,
  addError,
}) => {
  const [internalEntries, setInternalEntries] = useState<[string, string, PlatformJsonSchema][]>(
    Object.entries(jsonSchema.properties).map(([prop, schema]) => [
      schema.id || generateWorkflowToken(),
      prop,
      schema,
    ]),
  );
  const [requiredProps, setRequiredProps] = useState(jsonSchema.required || []);

  const isEmptyObject = (schema: PlatformJsonSchema) => {
    return (
      // empty object
      (schema.type === JsonSchemaTypes.Object &&
        !('objectType' in schema.properties) &&
        Object.entries(schema.properties).length === 0) ||
      // array of empty objects
      (schema.type === JsonSchemaTypes.Array &&
        schema.items.type === JsonSchemaTypes.Object &&
        !('objectType' in schema.items.properties) &&
        Object.entries(schema.items.properties).length === 0)
    );
  };

  const propertyErrors = useMemo<ErrorType[][]>(() => {
    return internalEntries.map((entry) => {
      const name = entry[1];
      const schema = entry[2];
      return [
        ...(name.length === 0 ? [ErrorType.BlankName] : []),
        ...(internalEntries.filter((e) => e[1] === name).length > 1
          ? [ErrorType.DuplicateName]
          : []),
        ...(name.match(/[^a-z0-9_]/) ? [ErrorType.InvalidCharacter] : []),
        ...(isEmptyObject(schema) ? [ErrorType.EmptyObject] : []),
      ];
    });
  }, [internalEntries]);

  useEffect(() => {
    if (propertyErrors.every((errors) => errors.length === 0)) {
      const newProperties: Record<string, JsonSchemaCore> = internalEntries.reduce(
        (acc, [id, key, value]) => ({
          ...acc,
          [key]: {
            ...value,
            id,
          },
        }),
        {},
      );
      if (
        !isEqual(jsonSchema.properties, newProperties) ||
        !isEqual(jsonSchema.required || [], requiredProps)
      ) {
        updateJsonSchema({
          ...jsonSchema,
          type: JsonSchemaTypes.Object,
          properties: newProperties,
          required: requiredProps,
        });
      }
    }

    propertyErrors.forEach((errors, index) => {
      addError(internalEntries[index][0], errors);
    });
  }, [internalEntries, propertyErrors, updateJsonSchema, jsonSchema, requiredProps, addError]);

  return (
    <Fragment>
      {internalEntries.map(([id, property, schema], index) => {
        return (
          <PropertyRow
            key={id}
            editableId={editableId}
            hasError={propertyErrors[index].length > 0}
            id={id}
            includePersonaObjects={includePersonaObjects}
            nestLevel={nestLevel}
            prefix={prefix}
            property={property}
            requiredProps={requiredProps}
            schema={schema}
            templateOptionsByObjectKey={templateOptionsByObjectKey}
            onAddProperty={(newProperty) => {
              const newProp = newProperty || createNewProperty();
              setInternalEntries((currentEntries) => [
                ...currentEntries.slice(0, index + 1),
                [newProp.id, newProp.name, newProp.schema],
                ...currentEntries.slice(index + 1),
              ]);
              setEditableId(newProp.id);
              setRequiredProps([...requiredProps, newProp.name]);
            }}
            onChangeProperty={(newProperty) => {
              setInternalEntries((currentEntries) =>
                Object.assign([], currentEntries, {
                  [index]: [currentEntries[index][0], newProperty, currentEntries[index][2]],
                }),
              );
            }}
            onChangeSchema={(newSchema) => {
              setInternalEntries((currentEntries) =>
                Object.assign([], currentEntries, {
                  [index]: [currentEntries[index][0], currentEntries[index][1], newSchema],
                }),
              );
            }}
            onDeleteProperty={() =>
              internalEntries.length > 0 &&
              setInternalEntries((currentEntries) => [
                ...currentEntries.slice(0, index),
                ...currentEntries.slice(index + 1),
              ])
            }
            onUpdateRequired={setRequiredProps}
            setEditableId={setEditableId}
            setOutsideClickable={setOutsideClickable}
            addError={addError}
          />
        );
      })}
    </Fragment>
  );
};

export default SchemaProperties;
